import React, { useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import ChartTable from "../chart-table"; // Adjust the path
import {
  MenuItem,
  Select,
  Checkbox,
  ListItemText,
  InputLabel,
  FormControl,
  Grid,
  Typography,
  Box,
} from "@mui/material";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const StockDetailsWrtPlantAndListNumber = ({ data }) => {
  const allOEMs = Object.keys(data || {});
  const [selectedOEMs, setSelectedOEMs] = useState(["TAFE"]);

  const handleOEMChange = (event) => {
    const {
      target: { value },
    } = event;
    setSelectedOEMs(typeof value === "string" ? value.split(",") : value);
  };

  const getChartAndTableData = (oemData) => {
    if (!oemData || Object.keys(oemData).length === 0) {
      return { chartData: null, tableHeaders: [], tableRows: [] };
    }

    const allListNumbers = Object.keys(oemData);
    const plantSet = new Set();

    allListNumbers.forEach((listNo) => {
      const plantObj = oemData[listNo] || {};
      Object.keys(plantObj).forEach((plant) => plantSet.add(plant));
    });

    const allPlants = Array.from(plantSet);
    const sortedListNumbers = [...allListNumbers].sort();

    // Prepare chart data
    const datasets = allPlants.map((plant, index) => {
      const colorPalette = [
        "#457b9d",
        "#e63946",
        "#2a9d8f",
        "#f4a261",
        "#ffb703",
        "#6a4c93",
      ];
      const dataPoints = sortedListNumbers.map(
        (listNo) => oemData[listNo]?.[plant] || 0
      );
      return {
        label: plant,
        data: dataPoints,
        backgroundColor: colorPalette[index % colorPalette.length],
        barThickness: 30, // reduced bar thickness
      };
    });

    const tableHeaders = ["", ...sortedListNumbers];
    const tableRows = allPlants.map((plant) => {
      const row = [plant];
      sortedListNumbers.forEach((listNo) => {
        const val = oemData[listNo]?.[plant] || 0;
        row.push(val);
      });
      return row;
    });

    return {
      chartData: {
        labels: sortedListNumbers,
        datasets,
      },
      tableHeaders,
      tableRows,
    };
  };

  const options = {
    responsive: true,
    plugins: {
      legend: { position: "top" },
      tooltip: {},
      datalabels: {
        display: false,
      },
      datalabels: {
        color: "#ffffff",
        anchor: "center",
        align: "center",
        font: {
          weight: "bold",
        },
      },
    },
    scales: {
      x: {
        ticks: {
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
        },
        grid: {
          display: false,
          drawBorder: false,
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: "#e0e0e0", // optional: light gray horizontal lines
          drawBorder: false, // optional: hide y-axis line
        },
      },
    },
    layout: {
      padding: {
        top: 10,
        bottom: 10,
      },
    },
  };

  return (
    <div>
      <Box sx={{ position: "absolute", right: "60px" }}>
        <FormControl sx={{ m: 1, width: 300 }} size="small">
          <InputLabel>OEMs</InputLabel>
          <Select
            multiple
            value={selectedOEMs}
            onChange={handleOEMChange}
            renderValue={(selected) => selected.join(", ")}
            label="OEMs"
          >
            {allOEMs.map((oem) => (
              <MenuItem key={oem} value={oem}>
                <Checkbox checked={selectedOEMs.includes(oem)} />
                <ListItemText primary={oem} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Grid container spacing={4}>
        {selectedOEMs.length > 0 ? (
          selectedOEMs.map((oem) => {
            const { chartData, tableHeaders, tableRows } = getChartAndTableData(
              data[oem]
            );

            return (
              <Grid
                key={oem}
                item
                xs={12}
                md={selectedOEMs.length > 1 ? 6 : 12}
              >
                <div>
                  <Typography variant="h6">{oem}</Typography>
                  {chartData ? (
                    <>
                      <div>
                        <Bar data={chartData} options={options} height={90} />
                      </div>
                      <div style={{ marginTop: "10px" }}>
                        <ChartTable headers={tableHeaders} rows={tableRows} />
                      </div>
                    </>
                  ) : (
                    <p>No data available for {oem}</p>
                  )}
                </div>
              </Grid>
            );
          })
        ) : (
          <Typography
            variant="h6"
            sx={{
              mt: 5,
              display: "flex",
              justifyContent: "center",
              width: "100%",
            }}
          >
            Select Oem for Data
          </Typography>
        )}
      </Grid>
    </div>
  );
};

export default StockDetailsWrtPlantAndListNumber;
